import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const DEFAULT_CURRENCY_DECIMALS = 2;
class FreeShippingProgress extends Component {
  requiredRefs = ['progressFill', 'messageRemaining', 'messageAchieved', 'moneyFormat'];
  countryShippingValue = null;
  countryFreeShippingValue = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.#observeCartTotal();

    this.#initializeCountryAndProgress();
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (this.#cartTotalObserver) {
      this.#cartTotalObserver.disconnect();
    }
  }

  async #initializeCountryAndProgress() {
    const savedCountry = localStorage.getItem('userCountry');
    if (savedCountry) {
      this.#checkCountryInSettings();
    } else {
      await this.#fetchAndSaveCountry();
    }

    requestAnimationFrame(() => this.updateProgress());
  }

  #handleCartUpdate = (event) => {
    setTimeout(() => {
      if (event && event.detail && event.detail.resource) {
        const cartResource = event.detail.resource;
        if (cartResource.total_price !== undefined) {
          this.dataset.cartTotal = cartResource.total_price.toString();
        }
      } else {
        this.#updateCartTotal();
      }

      this.updateProgress();
    }, 100);
  };

  #updateCartTotal() {
    const cartTotalElement = document.querySelector('[data-cart-total]');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartIcon = document.querySelector('cart-icon');

    let newCartTotal = null;

    if (cartDrawer && cartDrawer.dataset.cartTotal) {
      newCartTotal = cartDrawer.dataset.cartTotal;
    } else if (cartIcon && cartIcon.dataset.cartTotal) {
      newCartTotal = cartIcon.dataset.cartTotal;
    } else if (cartTotalElement && cartTotalElement.dataset.cartTotal) {
      newCartTotal = cartTotalElement.dataset.cartTotal;
    }

    if (newCartTotal !== null) {
      this.dataset.cartTotal = newCartTotal;
    }
  }

  #cartTotalObserver;

  #observeCartTotal() {
    this.#cartTotalObserver = new MutationObserver(() => {
      this.updateProgress();
    });

    this.#cartTotalObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-cart-total', 'data-shipping-minimum'],
    });
  }

  async #fetchAndSaveCountry() {
    const savedCountry = localStorage.getItem('userCountry');
    if (savedCountry) {
      return;
    }

    try {
      const response = await fetch('https://get.geojs.io/v1/ip/geo.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const country = data.country;

      if (country) {
        localStorage.setItem('userCountry', country);
        this.#checkCountryInSettings();
      }
    } catch (error) {}
  }

  #checkCountryInSettings() {
    const userCountryRaw = localStorage.getItem('userCountry');

    if (!userCountryRaw) {
      return;
    }

    if (!window.allowedCountries || typeof window.allowedCountries !== 'object') {
      setTimeout(() => this.#checkCountryInSettings(), 500);
      return;
    }

    const userCountryISO = this.#getCountryISOCode(userCountryRaw);

    if (!userCountryISO) {
      return;
    }

    try {
      const allowedCountries = window.allowedCountries;

      if (allowedCountries && typeof allowedCountries === 'object') {
        let foundRegion = null;

        for (const regionKey in allowedCountries) {
          const region = allowedCountries[regionKey];
          if (region && Array.isArray(region.countries)) {
            const countryFound = region.countries.some(
              (country) => country.toUpperCase() === userCountryISO.toUpperCase(),
            );

            if (countryFound) {
              foundRegion = region;
              break;
            }
          }
        }

        if (foundRegion) {
          this.countryShippingValue = foundRegion.shipping_price;
          this.countryFreeShippingValue = foundRegion.free_shipping_threshold;

          this.#updateMessageWithCountry();
          this.updateProgress();
        }
      }
    } catch (error) {}
  }

  #capitalize(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  #getCountryISOCode(countryName) {
    const countryMap = {
      Austria: 'AUT',
      Slovenia: 'SVN',
      Hungary: 'HUN',
      'Czech Republic': 'CZE',
      Czechia: 'CZE',
      Poland: 'POL',
      Slovakia: 'SVK',
      Bulgaria: 'BGR',
      Germany: 'DEU',
      Romania: 'ROU',
      Portugal: 'PRT',
      Italy: 'ITA',
      Latvia: 'LVA',
      Lithuania: 'LTU',
      Luxembourg: 'LUX',
      Netherlands: 'NLD',
      'The Netherlands': 'NLD',
      Belgium: 'BEL',
      France: 'FRA',
      Ireland: 'IRL',
      Switzerland: 'CHE',
      Spain: 'ESP',
      Denmark: 'DNK',
      Estonia: 'EST',
      Liechtenstein: 'LIE',
      Greece: 'GRC',
      Sweden: 'SWE',
      Finland: 'FIN',
      Norway: 'NOR',
      Malta: 'MLT',
      Cyprus: 'CYP',
      Croatia: 'HRV',
      Turkey: 'TUR',
      Türkiye: 'TUR',
      Albania: 'ALB',
      Andorra: 'AND',
      'Bosnia and Herzegovina': 'BIH',
      Gibraltar: 'GIB',
      Iceland: 'ISL',
      Kosovo: 'XKX',
      Serbia: 'SRB',
      Israel: 'ISR',
      'North Macedonia': 'MKD',
      Montenegro: 'MNE',
      Monaco: 'MCO',
      'United Kingdom': 'GBR',
      UK: 'GBR',
      Canada: 'CAN',
      'United States': 'USA',
      USA: 'USA',
      US: 'USA',
      Australia: 'AUS',
      Japan: 'JPN',
      Malaysia: 'MYS',
      Singapore: 'SGP',
      'South Korea': 'KOR',
      Korea: 'KOR',
      China: 'CHN',
      India: 'IND',
      Philippines: 'PHL',
      Taiwan: 'TWN',
      'Hong Kong': 'HKG',
    };
    if (countryMap[countryName]) {
      return countryMap[countryName];
    }

    const lowerCountryName = countryName.toLowerCase();
    for (const [name, code] of Object.entries(countryMap)) {
      if (name.toLowerCase() === lowerCountryName) {
        return code;
      }
    }

    return null;
  }

  #updateCartSummaryValue(shippingCost = null) {
    requestAnimationFrame(() => {
      const valueElement = document.querySelector('.cart__free-shipping-progress-value');
      const withShippingElement = document.querySelector('.cart__total-value--with-shipping');
      const regularElement = document.querySelector('.cart__total-value');

      if (!valueElement) {
        return;
      }

      if (shippingCost === null || shippingCost === undefined) {
        return;
      }

      const shippingInCents = shippingCost * 100;
      valueElement.textContent = this.#formatMoney(shippingInCents);

      if (shippingCost > 0) {
        if (withShippingElement instanceof HTMLElement) {
          const cartTotal = parseInt(this.dataset.cartTotal || '0', 10);
          const totalWithShipping = cartTotal + shippingInCents;
          withShippingElement.textContent = this.#formatMoney(totalWithShipping);
          withShippingElement.classList.remove('hidden');
        }
        if (regularElement instanceof HTMLElement) {
          regularElement.classList.add('hidden');
        }
      } else {
        if (regularElement instanceof HTMLElement) {
          regularElement.classList.remove('hidden');
        }
        if (withShippingElement instanceof HTMLElement) {
          withShippingElement.classList.add('hidden');
        }
      }
    });
  }

  #updateMessageWithCountry() {
    requestAnimationFrame(() => {
      const userCountry = localStorage.getItem('userCountry');

      if (!userCountry || !this.refs.messageAchieved) {
        return;
      }

      const capitalizedCountry = this.#capitalize(userCountry);
      const messageElement = Array.isArray(this.refs.messageAchieved)
        ? this.refs.messageAchieved[0]
        : this.refs.messageAchieved;

      if (!messageElement || !(messageElement instanceof HTMLElement)) {
        return;
      }

      const originalText = messageElement.textContent || messageElement.innerText || '';

      if (!originalText.trim()) {
        return;
      }

      let updatedText = originalText;

      if (!originalText.includes(capitalizedCountry)) {
        updatedText = `${originalText} ${capitalizedCountry}`;
      }

      messageElement.textContent = updatedText;
    });
  }

  updateProgress() {
    const cartTotal = parseInt(this.dataset.cartTotal || '0', 10);

    if (this.countryFreeShippingValue === null || this.countryFreeShippingValue === undefined) {
      this.classList.add('hidden');
      return;
    }

    const shippingMinimum = this.countryFreeShippingValue * 100;

    if (!shippingMinimum || shippingMinimum === 0) {
      this.classList.add('hidden');
      return;
    }

    this.classList.remove('hidden');

    const progress = Math.min(100, Math.max(0, (cartTotal / shippingMinimum) * 100));
    const hasFreeShipping = cartTotal >= shippingMinimum;

    if (this.refs.progressFill) {
      this.refs.progressFill.style.setProperty('--progress', `${progress}%`);
      this.refs.progressFill.classList.toggle('free-shipping-progress__fill--complete', hasFreeShipping);
    }

    if (this.refs.messageRemaining) {
      this.refs.messageRemaining.classList.toggle('hidden', hasFreeShipping);
      const remaining = shippingMinimum - cartTotal;
      this.refs.messageRemaining.textContent = this.#formatMoney(remaining);
    }

    if (this.refs.messageAchieved) {
      this.refs.messageAchieved.classList.toggle('free-shipping-progress__message--achieved', hasFreeShipping);
    }

    if (hasFreeShipping) {
      this.#updateCartSummaryValue(0);
    } else if (this.countryShippingValue) {
      this.#updateCartSummaryValue(this.countryShippingValue);
    }

    this.dataset.progress = Math.round(progress);
    this.classList.toggle('free-shipping-progress--complete', hasFreeShipping);

    this.#updateMessageWithCountry();
  }

  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      const format = this.#getFormatOptions(placeholder);
      return this.#formatCents(moneyValue, format.thousandsSeparator, format.decimalSeparator, format.precision);
    });
  }

  #getFormatOptions(placeholder) {
    const formats = {
      amount: { thousandsSeparator: ',', decimalSeparator: '.', precision: DEFAULT_CURRENCY_DECIMALS },
      amount_no_decimals: { thousandsSeparator: ',', decimalSeparator: '.', precision: 0 },
      amount_with_comma_separator: {
        thousandsSeparator: '.',
        decimalSeparator: ',',
        precision: DEFAULT_CURRENCY_DECIMALS,
      },
      amount_no_decimals_with_comma_separator: { thousandsSeparator: '.', decimalSeparator: ',', precision: 0 },
      amount_no_decimals_with_space_separator: { thousandsSeparator: ' ', decimalSeparator: '.', precision: 0 },
      amount_with_space_separator: {
        thousandsSeparator: ' ',
        decimalSeparator: ',',
        precision: DEFAULT_CURRENCY_DECIMALS,
      },
      amount_with_period_and_space_separator: {
        thousandsSeparator: ' ',
        decimalSeparator: '.',
        precision: DEFAULT_CURRENCY_DECIMALS,
      },
      amount_with_apostrophe_separator: {
        thousandsSeparator: "'",
        decimalSeparator: '.',
        precision: DEFAULT_CURRENCY_DECIMALS,
      },
    };

    return formats[placeholder] ?? formats.amount;
  }

  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
